import React from "react";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  LAM_LINE_SELECTION_LABEL,
  type LamLineSelection,
} from "../../api/lamLine";

/**
 * Lam 1 / Lam 2 / Both switch, shared by the overview cards that can show
 * either line or the two of them read as one plant total.
 *
 * It sits beside the card's title rather than out with the other controls: it
 * changes *what* the card is about, not how that thing is displayed, and the
 * heading has to be read together with it.
 *
 * `onChange` only fires for a real change — the group is exclusive, so clicking
 * the selected button would otherwise report null and clear the selection.
 */
const LINE_OPTIONS: LamLineSelection[] = ["lam1", "lam2", "both"];

const LineSelector: React.FC<{
  value: LamLineSelection;
  onChange: (line: LamLineSelection) => void;
}> = ({ value, onChange }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {/* Named in front of the buttons: "Lam 1 / Lam 2 / Both" alone reads as a
        set of places, not as a choice of what the card is describing. */}
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
      aria-hidden
    >
      Line:
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v: LamLineSelection | null) => {
        if (v && v !== value) onChange(v);
      }}
      aria-label="Lamination line"
    >
      {LINE_OPTIONS.map((l) => (
        <ToggleButton key={l} value={l}>
          {LAM_LINE_SELECTION_LABEL[l]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Stack>
);

export default LineSelector;
